import { apiCall } from '@/util/api';
import {
  NewsRunsSchema,
  type NewsRuns,
  type NewsRunSummary,
} from './news_runs_schema';

// News runs / leaderboard (#447): GET /api/topics/{id}/sharing-runs.
// The wire schema/types live in ./news_runs_schema; this file is the fetch
// + formatting logic.

export type { NewsRuns, NewsRunSummary } from './news_runs_schema';
export { NewsRunsSchema } from './news_runs_schema';

// The name a leaderboard row shows for a run whose starter gave no allowed
// name. It is the word the backend tells a nameless sender's reshare story in
// (defaultAllowedSenderName, internal/share/story.go), capitalized to open a
// table cell.
const ANONYMOUS_SENDER_NAME = 'Someone';

export const NewsRunsLogic = {
  async fetch(topicId: string, signal?: AbortSignal): Promise<NewsRuns> {
    const encoded = encodeURIComponent(topicId);
    const response = await apiCall(`/api/topics/${encoded}/sharing-runs`, {
      signal,
    });
    if (!response.ok) {
      throw new Error(`Failed to load news runs (${response.status})`);
    }
    const data = await response.json();
    return NewsRunsSchema.parse(data);
  },

  /**
   * Who started a run, for the leaderboard's "Who" cell: their allowed name,
   * or "Someone" when they gave none (the field is optional when sharing).
   */
  senderName(run: Pick<NewsRunSummary, 'allowedSenderName'>): string {
    return run.allowedSenderName.trim() || ANONYMOUS_SENDER_NAME;
  },

  /** "3d 4h" / "2h 5m" / "12m" — compact time-to-goal for a leaderboard cell. */
  formatTimeToGoal(seconds: number): string {
    const days = Math.floor(seconds / 86_400);
    const hours = Math.floor((seconds % 86_400) / 3_600);
    const minutes = Math.floor((seconds % 3_600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${Math.max(1, minutes)}m`;
  },
};
